import { useAuth } from "@/auth/AuthContext";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { EdictaLogo, NAV_ICONS, type NavIcon } from "@/components/layout/navIcons";
import { Button } from "@/components/ui/Button";
import { ADMIN_NAV, ADMIN_ROLE } from "@/lib/adminNav";
import { appVersion } from "@/lib/appVersion";
import { cn } from "@/lib/cn";
import { useState } from "react";
import { NavLink, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";

type NavItem = { to: string; label: string; icon: NavIcon; end?: boolean };

const WORK_NAV: NavItem[] = [
  { to: "/", label: "Mails", icon: "mails", end: true },
  { to: "/archive", label: "Archiv", icon: "archive" },
  { to: "/queue", label: "Queue", icon: "queue" },
  { to: "/bounces", label: "Rückläufer", icon: "bounces" },
];

const ADMIN_ICONS: Record<string, NavIcon> = {
  "/admin/sources": "sources",
  "/admin/templates": "templates",
  "/admin/senders": "senders",
  "/admin/users": "users",
  "/admin/api-keys": "apiKeys",
  "/admin/audit": "audit",
};

function NavGroup({
  label,
  items,
  defaultOpen = true,
}: {
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}) {
  const { pathname } = useLocation();
  // A collapsed group still opens when the page shown is one of its entries,
  // so the active item is never hidden (e.g. a reload on /admin/users).
  const containsActive = items.some((item) => matchPath({ path: item.to, end: item.end ?? false }, pathname));
  const [open, setOpen] = useState(defaultOpen || containsActive);
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted hover:text-ink"
        onClick={() => setOpen((value) => !value)}
      >
        <svg
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={cn("h-2.5 w-2.5 transition-transform", !open && "-rotate-90")}
          aria-hidden
        >
          <path d="M2 3.5l3 3 3-3" />
        </svg>
        {label}
      </button>
      {open
        ? items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-sm",
                  isActive
                    ? "bg-accent-dim font-medium text-accent shadow-[inset_2px_0_0_var(--color-accent)]"
                    : "text-ink hover:bg-surface-subtle",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className={cn("h-4 w-4 shrink-0", isActive ? "text-accent" : "text-muted")}>
                    {NAV_ICONS[item.icon]}
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))
        : null}
    </div>
  );
}

export function Shell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const adminItems: NavItem[] = ADMIN_NAV.map((item) => ({ ...item, icon: ADMIN_ICONS[item.to] }));

  return (
    <div className="flex min-h-screen bg-bg text-ink">
      <aside className="sticky top-0 flex h-screen w-[232px] shrink-0 flex-col gap-3.5 border-r border-hairline bg-surface px-2.5 pb-2.5 pt-3.5">
        <NavLink to="/" className="flex items-center gap-2.5 px-2 py-0.5">
          <EdictaLogo className="h-5 w-5 text-accent" />
          <span className="text-base font-bold tracking-tight">Edicta</span>
        </NavLink>

        <Button variant="primary" className="w-full" onClick={() => navigate("/mails/new")}>
          Neue Mail
        </Button>

        <nav aria-label="Hauptnavigation" className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto">
          <NavGroup label="Arbeitsbereich" items={WORK_NAV} />
          {user?.role === ADMIN_ROLE ? <NavGroup label="Administration" items={adminItems} defaultOpen={false} /> : null}
        </nav>

        <div className="flex flex-col gap-2 border-t border-hairline pt-2">
          <AccountMenu />
          <p
            className="px-2 pb-0.5 text-[10px] leading-none text-muted opacity-70"
            data-testid="app-version"
            title={appVersion.sha ? `commit ${appVersion.sha}` : "local dev build"}
          >
            Edicta {appVersion.label}
          </p>
        </div>
      </aside>
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}
